import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BENEFITS_URL } from 'src/config-global';
import { AxiosHttpClient } from 'src/http';
import { BenefitsRepository } from 'src/repositories/benefits/benefits.repository';
import { useMyDataStore } from 'src/store/me/me.store';
import { LPApprovalStatus, LPBenefitListItem, LPBenefitsResponse } from 'src/types/lp-benefits';

const repository = new BenefitsRepository(new AxiosHttpClient(BENEFITS_URL));
const statuses: LPApprovalStatus[] = ['pending', 'approved', 'declined'];

export default function useLPBenefitsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('approvalStatus') as LPApprovalStatus;
  const approvalStatus = statuses.includes(statusParam) ? statusParam : 'pending';
  const [benefits, setBenefits] = useState<LPBenefitsResponse>();
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [updatingBenefitIds, setUpdatingBenefitIds] = useState<string[]>([]);
  const statusRequests = useRef(new Set<string>());
  const user = useMyDataStore((state) => state.me);
  const canChangeBenefitStatus =
    approvalStatus === 'approved' && Boolean(user?.roles?.includes('cn_admin'));

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError('');
    const timeout = setTimeout(async () => {
      try {
        const response = await repository.getLPBenefits({
          approvalStatus,
          search,
          sort: JSON.stringify({ sortBy: sortModel[0]?.field, direction: sortModel[0]?.sort }),
          ...paginationModel,
        });
        if (!active) return;
        if (response.isLeft()) throw new Error(response.value.message);
        if (!response.value) throw new Error('Não foi possível carregar os benefícios.');
        // Handle deleting/filtering the last item on a page without leaving an empty page behind.
        if (
          paginationModel.page > 0 &&
          !response.value.data.length &&
          response.value.totalFiltered > 0
        ) {
          setPaginationModel((current) => ({ ...current, page: response.value!.totalPages - 1 }));
        } else {
          setBenefits(response.value);
        }
      } catch (cause) {
        if (active) {
          const message = cause instanceof Error ? cause.message : 'Erro ao buscar benefícios.';
          setBenefits(undefined);
          setError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [approvalStatus, paginationModel, search, sortModel, user?._id, refreshVersion]);

  const onChangeBenefitStatus = useCallback(
    async (benefit: LPBenefitListItem, newStatus: 'active' | 'inactive') => {
      if (!canChangeBenefitStatus || benefit.lpEventsData?.approvalStatus !== 'approved') return;
      const key = `${benefit.benefitType}:${benefit.benefitId}`;
      if (statusRequests.current.has(key)) return;
      statusRequests.current.add(key);
      setUpdatingBenefitIds((current) => [...current, key]);
      try {
        const response = await repository.changeLPBenefitStatus({
          benefitId: benefit.benefitId,
          benefitType: benefit.benefitType,
          newStatus,
        });
        if (response.isLeft()) throw new Error(response.value.message);
        if (!response.value) throw new Error('Não foi possível atualizar o status do benefício.');
        setRefreshVersion((current) => current + 1);
        enqueueSnackbar('Status do benefício atualizado com sucesso!', { variant: 'success' });
      } catch (cause) {
        enqueueSnackbar(
          cause instanceof Error ? cause.message : 'Erro ao atualizar o status do benefício.',
          {
            variant: 'error',
          }
        );
      } finally {
        statusRequests.current.delete(key);
        setUpdatingBenefitIds((current) => current.filter((id) => id !== key));
      }
    },
    [canChangeBenefitStatus]
  );

  const resetPage = () => setPaginationModel((current) => ({ ...current, page: 0 }));

  return {
    benefits,
    approvalStatus,
    sortModel,
    paginationModel,
    search,
    isLoading,
    error,
    canChangeBenefitStatus,
    updatingBenefitIds,
    onChangeBenefitStatus,
    roles: user?.roles,
    canCreate: user?.roles?.some((role) =>
      ['lp_shopkeeper', 'lp_exhibitor', 'lp_contractor'].includes(role)
    ),
    setPaginationModel,
    onChangeStatus: (status: LPApprovalStatus) => {
      resetPage();
      setSearchParams({ approvalStatus: status });
    },
    onSearch: (value: string) => {
      resetPage();
      setSearch(value);
    },
    onSort: (value: GridSortModel) => {
      resetPage();
      setSortModel(value);
    },
  };
}
